import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { getAccessToken } from "../../api/client"
import { Icon } from "../../components/Icon"
import { IconButton } from "../../components/ui"

export type TaskMedia = { url: string; type: "IMAGE" | "VIDEO" }

export function TaskMediaGallery({ media, emptyMessage }: { media: TaskMedia[]; emptyMessage?: string }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  if (!media.length) return emptyMessage ? <p className="muted">{emptyMessage}</p> : null
  return (
    <>
      <div className="completion-media">
        {media.map((item, index) => (
          <button
            type="button"
            className="completion-media__item"
            key={`${item.url}:${index}`}
            aria-label={`Открыть ${item.type === "VIDEO" ? "видео" : "фото"} ${index + 1} из ${media.length}`}
            onClick={() => setActiveIndex(index)}
          >
            <AuthenticatedMedia media={item} variant="thumbnail" />
            {item.type === "VIDEO" ? <span className="completion-media__play" aria-hidden="true">▶</span> : null}
            <span className="completion-media__number">{index + 1}</span>
          </button>
        ))}
      </div>
      {activeIndex !== null ? (
        <MediaLightbox
          media={media}
          activeIndex={activeIndex}
          onActiveIndexChange={setActiveIndex}
          onClose={() => setActiveIndex(null)}
        />
      ) : null}
    </>
  )
}

function MediaLightbox({
  media,
  activeIndex,
  onActiveIndexChange,
  onClose
}: {
  media: TaskMedia[]
  activeIndex: number
  onActiveIndexChange(index: number): void
  onClose(): void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const current = media[activeIndex]
  const move = (offset: number) => onActiveIndexChange((activeIndex + offset + media.length) % media.length)

  useEffect(() => {
    const dialog = ref.current
    if (dialog && !dialog.open) dialog.showModal()
    return () => {
      if (dialog?.open) dialog.close()
    }
  }, [])

  useEffect(() => {
    function handleKeydown(event: KeyboardEvent) {
      if (event.key === "ArrowLeft" && media.length > 1) move(-1)
      if (event.key === "ArrowRight" && media.length > 1) move(1)
    }
    window.addEventListener("keydown", handleKeydown)
    return () => window.removeEventListener("keydown", handleKeydown)
  })

  if (!current) return null
  return createPortal(
    <dialog
      ref={ref}
      className="media-lightbox"
      aria-label={`Просмотр вложения ${activeIndex + 1} из ${media.length}`}
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <header className="media-lightbox__header">
        <div>
          <strong>{current.type === "VIDEO" ? "Видео" : "Фото"}</strong>
          <span>{activeIndex + 1} из {media.length}</span>
        </div>
        <IconButton type="button" label="Закрыть галерею" icon="x" onClick={onClose} />
      </header>
      <div className="media-lightbox__stage">
        {media.length > 1 ? (
          <button
            type="button"
            className="media-lightbox__nav media-lightbox__nav--prev"
            aria-label="Предыдущее вложение"
            onClick={() => move(-1)}
          >
            <Icon name="chevronRight" />
          </button>
        ) : null}
        <AuthenticatedMedia key={current.url} media={current} variant="viewer" />
        {media.length > 1 ? (
          <button
            type="button"
            className="media-lightbox__nav media-lightbox__nav--next"
            aria-label="Следующее вложение"
            onClick={() => move(1)}
          >
            <Icon name="chevronRight" />
          </button>
        ) : null}
      </div>
      {media.length > 1 ? (
        <div className="media-lightbox__strip" aria-label="Все вложения">
          {media.map((item, index) => (
            <button
              type="button"
              className={index === activeIndex ? "is-active" : ""}
              key={`${item.url}:lightbox:${index}`}
              aria-label={`Показать ${item.type === "VIDEO" ? "видео" : "фото"} ${index + 1}`}
              aria-current={index === activeIndex ? "true" : undefined}
              onClick={() => onActiveIndexChange(index)}
            >
              <AuthenticatedMedia media={item} variant="thumbnail" />
              {item.type === "VIDEO" ? <span aria-hidden="true">▶</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </dialog>,
    document.body
  )
}

function AuthenticatedMedia({ media, variant }: { media: TaskMedia; variant: "thumbnail" | "viewer" }) {
  const [source, setSource] = useState("")
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    const controller = new AbortController()
    let objectUrl = ""
    const token = getAccessToken()
    void fetch(media.url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: controller.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error("Media unavailable")
        return response.blob()
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob)
        setSource(objectUrl)
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) setFailed(true)
      })
    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [media.url])

  if (failed) return <div className="media-placeholder media-placeholder--error">Не удалось загрузить вложение</div>
  if (!source) return <div className="media-placeholder">Загрузка вложения…</div>
  return media.type === "VIDEO" ? (
    <video
      src={source}
      controls={variant === "viewer"}
      muted={variant === "thumbnail"}
      playsInline
      preload="metadata"
    />
  ) : (
    <img src={source} alt="Вложение к результату задачи" />
  )
}
